import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AccessibilityHelper = ({ 
  onQuickFilter, 
  onClearFilters, 
  onViewModeChange, 
  totalApplications 
}) => {
  const [showHelp, setShowHelp] = useState(false);
  const [announcements, setAnnouncements] = useState([]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((event) => {
    // Check if user is typing in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    if (event.altKey) {
      switch (event.key.toLowerCase()) {
        case 'f':
          event.preventDefault();
          onQuickFilter('favorites');
          announce('Switched to favorites filter');
          break;
        case 'r':
          event.preventDefault();
          onQuickFilter('recent');
          announce('Switched to recent applications filter');
          break;
        case 'c':
          event.preventDefault();
          onClearFilters();
          announce('All filters cleared');
          break;
        case 'g':
          event.preventDefault();
          onViewModeChange('grid');
          announce('Switched to grid view');
          break;
        case 't':
          event.preventDefault();
          onViewModeChange('timeline');
          announce('Switched to timeline view');
          break;
        case 'm':
          event.preventDefault();
          onViewModeChange('map');
          announce('Switched to map view');
          break;
        case 'n':
          event.preventDefault();
          onViewModeChange('newspaper');
          announce('Switched to newspaper view');
          break;
        case 's': {
          event.preventDefault();
          const searchInput = document.querySelector('input[type="search"], input[placeholder*="search" i]');
          if (searchInput) {
            searchInput.focus();
            announce('Search input focused');
          }
          break;
        }
        case 'h':
          event.preventDefault();
          setShowHelp(prev => !prev);
          announce(showHelp ? 'Help panel closed' : 'Help panel opened');
          break;
        default:
          break;
      }
    }
  }, [onQuickFilter, onClearFilters, onViewModeChange, showHelp]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const announce = (message) => {
    setAnnouncements(prev => [...prev, { id: Date.now(), message }]);
    setTimeout(() => {
      setAnnouncements(prev => prev.slice(1));
    }, 3000);
  };

  const shortcuts = [
    { keys: 'Alt + F', action: 'Filter by Favorites' },
    { keys: 'Alt + R', action: 'Filter by Recent' },
    { keys: 'Alt + C', action: 'Clear All Filters' },
    { keys: 'Alt + G', action: 'Switch to Grid View' },
    { keys: 'Alt + T', action: 'Switch to Timeline View' },
    { keys: 'Alt + M', action: 'Switch to Map View' },
    { keys: 'Alt + N', action: 'Switch to Newspaper View' },
    { keys: 'Alt + S', action: 'Focus Search Input' },
    { keys: 'Alt + H', action: 'Toggle Help Panel' }
  ];

  return (
    <>
      {/* Screen Reader Announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true" 
        className="sr-only"
      >
        {announcements.map(announcement => (
          <div key={announcement.id}>
            {announcement.message}
          </div>
        ))}
      </div>

      {/* Page Status for Screen Readers */}
      <div 
        aria-live="polite" 
        aria-atomic="false" 
        className="sr-only"
      >
        {totalApplications > 0 && `Showing ${totalApplications} planning applications`}
      </div>

      {/* Keyboard Shortcuts Help Button */}
      <button
        onClick={() => setShowHelp(prev => !prev)}
        className="hidden md:flex fixed bottom-6 right-6 z-50 w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-lg transition-all duration-200 items-center justify-center"
        aria-label="Show keyboard shortcuts help"
        title="Keyboard shortcuts (Alt + H)"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Help Panel */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full max-h-96 overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Keyboard Shortcuts
                  </h3>
                  <button
                    onClick={() => setShowHelp(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Close help panel"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-3">
                  {shortcuts.map((shortcut, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {shortcut.action}
                      </span>
                      <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                        {shortcut.keys}
                      </kbd>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    These shortcuts work when not typing in input fields. Press Alt + H to toggle this help panel.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AccessibilityHelper; 
